import { useTranslation } from 'next-i18next';
import styles from './style.module.scss';
import Button, { TERTIARY_THEME } from 'components/control/button';
import NotificationIcon from 'components/icon/notification-icon';
import { searchFilterIds } from 'constants/test-constants';
import { LOCALE_SEARCH_PATHS, Namespaces, SiteLanguage } from 'types/locales';
import CloseIcon from 'components/icon/close-icon';
import { PartialDeep } from 'type-fest';
import { FilterItemData, ListingFilterBarType } from './listing_filter_bar';
import { useCallback } from 'react';
import { buildClassName } from 'utils/build-class-name';
import { createFilterConfigs } from './filter-chip-configs';
interface FilterItemProps {
  filter: FilterItemData;
  onValueChange: (filters: PartialDeep<ListingFilterBarType>, changedFilter?: string) => void;
  removeFilter: (filterType: FilterItemData['type']) => void;
  isSmall?: boolean;
}

const FilterItem: React.FC<FilterItemProps & { filterConfigs: ReturnType<typeof createFilterConfigs> }> = ({ filter, onValueChange, removeFilter, filterConfigs, isSmall }) => {
  const config = filterConfigs[filter.type];

  if (!config) {
    return null;
  }

  // City and province are display-only (not removable)
  const isDisplayOnly = filter.type === 'city' || filter.type === 'province' || filter.type === 'cityAndProvince';

  return (
    <div className={buildClassName(styles['active-filter-item'], isSmall && styles['small-active-filter-item'])}>
      <span className={styles['filter-value']}>{config.render(filter)}</span>
      {!isDisplayOnly && (
        <CloseIcon
          onClick={() => {
            config.removeAction(onValueChange);
            removeFilter(filter.type);
          }}
          className={buildClassName(styles['remove-filter-button'], isSmall && styles['small'])}
        />
      )}
    </div>
  );
};

interface FilterChipBarProps {
  removeFilterItem?: (filterType: FilterItemData['type']) => void;
  onValueChange: (filters: PartialDeep<ListingFilterBarType>, changedFilter?: string) => void;
  onSaveSearchButtonClick?: () => void;
  activeFilterItems: FilterItemData[];
  setActiveFilterItems: (activeFilterItems: FilterItemData[]) => void;
  filters: ListingFilterBarType;
  isMobile?: boolean;
  oneLine?: boolean;
  hideSaveSearchButton?: boolean;
  eventHandler?: {
    [key: string]: () => void;
  };
  isSmall?: boolean;
}

export const FilterChipBar = ({ onValueChange, onSaveSearchButtonClick, activeFilterItems, setActiveFilterItems, isMobile, oneLine = true, hideSaveSearchButton = false, eventHandler, isSmall = false }: FilterChipBarProps) => {
  const { t, i18n } = useTranslation(Namespaces.SEARCH);
  const filterConfigs = createFilterConfigs(t, i18n.language as SiteLanguage);

  const removeFilterItem = useCallback((filterType: FilterItemData['type']) => {
    const newFilterItems = activeFilterItems.filter(item => item.type !== filterType);
    setActiveFilterItems(newFilterItems);

    if (eventHandler) {
      eventHandler[`${filterType}Remove`]?.();
    }
  }, [activeFilterItems, eventHandler, setActiveFilterItems]);
  
  return (
    <div className={buildClassName(styles['active-filters-display'], !oneLine && styles['multi-line'])}>
      <>
        <div className={buildClassName(styles['active-filter-items-wrapper'], isSmall && styles['small-active-filter-items-wrapper'])}>
          {activeFilterItems.map((filter, index) => (
            <FilterItem
              key={`${filter.type}-${index}`}
              filter={filter}
              onValueChange={onValueChange}
              removeFilter={removeFilterItem}
              filterConfigs={filterConfigs}
              isSmall={isSmall}
            />
          ))}
        </div>
        {!isMobile && onSaveSearchButtonClick && !hideSaveSearchButton &&
          <Button
            theme={TERTIARY_THEME}
            label={t(LOCALE_SEARCH_PATHS.SAVED_SEARCH_BUTTON_SAVE, {
              defaultValue: 'Save Search',
            })}
            Icon={NotificationIcon}
            testId={searchFilterIds.saveSearchButtonMapPage}
            onClick={onSaveSearchButtonClick}
          />
        }
      </>
    </div>
  );
};
